/* Free-shipping progress bar component. Extracted from cart.js so it can also
   run on the PDP (which does not load cart.js). Re-renders on data-cart-total
   change; the PDP instance reconciles the real cart total from /cart.js. */
class FreeShippingThreshold extends HTMLElement {
  static get observedAttributes() {
    return ["data-cart-total"];
  }

  constructor() {
    super();
    const template = this.getElementsByTagName("template")[0];
    const shadowRoot = this.attachShadow({ mode: "open" });
    shadowRoot.appendChild(template.content.cloneNode(true));
  }

  connectedCallback() {
    this.render();
    if (this.hasAttribute("data-reconcile") && window.refreshFreeShippingBars) {
      window.refreshFreeShippingBars();
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "data-cart-total" && oldValue !== newValue && this.shadowRoot) {
      this.render();
    }
  }

  render() {
    const root = this.shadowRoot;
    if (!root) return;

    // Single-currency assumption: data-threshold and cart.total_price are both shop-currency
    // grosze, so Shopify.currency.rate === 1 today and this multiply is a no-op. If multi-currency
    // is ever enabled, revisit — total_price would be presentment-scaled and this multiply would
    // double-count the conversion.
    const threshold = Number(this.getAttribute("data-threshold")) * Shopify.currency.rate;
    const cartTotal = Number(this.getAttribute("data-cart-total"));
    const difference = threshold - cartTotal;

    // If either value is non-finite (missing/garbage attribute, Shopify.currency
    // not ready), keep the server-rendered DOM rather than rendering a wrong
    // "achieved" state — difference would be NaN, and NaN > 0 is false.
    if (!Number.isFinite(threshold) || !Number.isFinite(cartTotal)) return;

    // data-display selects the presentation: "text" → the PDP two-line message
    // (spec 09); anything else (the cart) → the progress bar.
    if (this.dataset.display === "text") {
      this.renderText(difference, cartTotal);
    } else {
      this.renderBar(difference, cartTotal, threshold);
    }
  }

  renderBar(difference, cartTotal, threshold) {
    const root = this.shadowRoot;
    const progress = root.querySelector(".fsb-progress");
    const achieved = root.querySelector(".fsb-achieved");
    const barOuter = root.querySelector(".free-shipping-bar__bar");
    const barInner = root.querySelector(".free-shipping-bar__bar-inner");
    const thresholdText = root.querySelector("[slot='threshold-text']");

    if (difference > 0) {
      const formatter = new Intl.NumberFormat("pl-PL", {
        style: "currency",
        currency: Shopify.currency.active,
        currencyDisplay: "narrowSymbol",
        maximumFractionDigits: 0,
      });
      // Intl.NumberFormat#format returns a plain string — textContent skips the HTML parser.
      thresholdText.textContent = formatter.format(difference / 100);
      barInner.style.width = `${Math.min(100, (cartTotal / threshold) * 100)}%`;
      progress.style.display = "";
      achieved.style.display = "none";
      barOuter.style.display = "";
    } else {
      progress.style.display = "none";
      achieved.style.display = "";
      barOuter.style.display = "none";
    }
  }

  renderText(difference, cartTotal) {
    const root = this.shadowRoot;
    const empty = root.querySelector(".fsm-empty");
    const progress = root.querySelector(".fsm-progress");
    const achieved = root.querySelector(".fsm-achieved");
    const line2 = root.querySelector(".fsm-line2");

    if (difference > 0) {
      // Whole-złoty amount for the PDP message ("150 zł"), distinct from the cart
      // bar's decimal formatter. textContent (not innerHTML) for the injected value.
      const formatter = new Intl.NumberFormat("pl-PL", {
        style: "currency",
        currency: Shopify.currency.active,
        currencyDisplay: "narrowSymbol",
        maximumFractionDigits: 0,
      });
      const amount = formatter.format(difference / 100);
      if (cartTotal === 0) {
        empty.querySelector(".fsm-amount").textContent = amount;
        empty.style.display = "";
        progress.style.display = "none";
      } else {
        progress.querySelector(".fsm-amount").textContent = amount;
        progress.style.display = "";
        empty.style.display = "none";
      }
      achieved.style.display = "none";
      line2.style.display = "";
    } else {
      empty.style.display = "none";
      progress.style.display = "none";
      achieved.style.display = "";
      line2.style.display = "none";
    }
  }
}

/* Refresh every reconcile-enabled bar (the PDP) from the authoritative cart.
   The cart page does not use this — it re-renders the bar server-side.
   Defined BEFORE customElements.define so the helper already exists when the
   define() call synchronously upgrades any <free-shipping-threshold data-reconcile>
   already in the DOM and its connectedCallback fires the load-time reconcile. */
window.refreshFreeShippingBars = function refreshFreeShippingBars() {
  const bars = document.querySelectorAll("free-shipping-threshold[data-reconcile]");
  if (!bars.length) return Promise.resolve();
  const cartUrl = (window.routes && window.routes.cart_url) || "/cart";
  return fetch(`${cartUrl}.js`, { headers: { Accept: "application/json" } })
    .then((response) => response.json())
    .then((cart) => {
      bars.forEach((bar) => bar.setAttribute("data-cart-total", String(cart.total_price)));
    })
    .catch(() => {
      /* On failure keep the server-rendered value — no throw. */
      /* eslint-disable no-console -- feature-detect + deliberate diagnostic trace for a swallowed fetch failure */
      if (window.console && console.debug)
        console.debug("refreshFreeShippingBars: /cart.js fetch failed; keeping server-rendered value");
      /* eslint-enable no-console */
    });
};

customElements.define("free-shipping-threshold", FreeShippingThreshold);
